import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { TrafoStation, Abgangsleitung } from '../../../types/network';
import { useNetworkElementsStore } from '../../../store/networkElementsStore';
import { useNetworkDataPolling } from '../../../hooks/useNetworkDataPolling';
import { useLoadFlowStore } from '../../../store/loadFlowStore';
import { useHistoricalPlaybackStore } from '../../../store/historicalPlaybackStore';
import { PowerFlowMetrics } from './trafostation/components/PowerFlowMetrics';
import { TrafoElectricalSection } from './trafostation/components/TrafoElectricalSection';
import { LemonbeatMeasurementsSection, type PhaseSummary } from './trafostation/components/LemonbeatMeasurementsSection';
import { ElementHistoryModal } from './shared/ElementHistoryModal';
import { StationDiagramModal } from './trafostation/components/StationDiagramModal';
import { CouplingsSection } from './shared/CouplingsSection';
import { OperationalRatingFactorField } from './shared/OperationalRatingFactorField';
import { findCablesConnectedToTrafo } from '../../../utils/networkConnectionUtils';
import {
  getLinkedOutletsByTrafo,
  type LinkedAmqpOutletsResponse,
  type OutletMeasurement
} from '../../../services/transformerAmqpService';
import { detailStyles, headerColors } from '../../../styles/detailStyles';
import { shortElementName } from '../../../utils/elementDisplayName';

interface Props {
  element: TrafoStation;
}

type PhaseKey = 'L1' | 'L2' | 'L3' | 'N';

const createEmptyPhaseSummary = (): PhaseSummary => ({
  L1: { current: null, activePower: null, reactivePower: null, apparentPower: null },
  L2: { current: null, activePower: null, reactivePower: null, apparentPower: null },
  L3: { current: null, activePower: null, reactivePower: null, apparentPower: null },
  N: { current: null, activePower: null, reactivePower: null, apparentPower: null }
});

const normalizeUnit = (unit?: string | null) => (unit ?? '').trim().toLowerCase();

const convertPowerToKw = (value: number, unit?: string | null) => {
  const normalized = normalizeUnit(unit);
  if (!normalized || normalized === 'w') return value / 1000;
  if (normalized === 'kw') return value;
  if (normalized === 'mw') return value * 1000;
  return value / 1000;
};

const convertReactiveToKvar = (value: number, unit?: string | null) => {
  const normalized = normalizeUnit(unit);
  if (!normalized || normalized === 'var') return value / 1000;
  if (normalized === 'kvar') return value;
  if (normalized === 'mvar') return value * 1000;
  return value / 1000;
};

const convertApparentToKva = (value: number, unit?: string | null) => {
  const normalized = normalizeUnit(unit);
  if (!normalized || normalized === 'va') return value / 1000;
  if (normalized === 'kva') return value;
  if (normalized === 'mva') return value * 1000;
  return value / 1000;
};

const convertCurrentToA = (value: number, unit?: string | null) => {
  const normalized = normalizeUnit(unit);
  if (!normalized || normalized === 'a') return value;
  if (normalized === 'ka') return value * 1000;
  return value;
};

const getPhaseMetric = (
  measurement: OutletMeasurement
): { phase: PhaseKey; metric: 'current' | 'active' | 'reactive' | 'apparent' } | null => {
  const type = (measurement.measurementType ?? '').toLowerCase();
  if (type === 'current_phase_a') return { phase: 'L1', metric: 'current' };
  if (type === 'current_phase_b') return { phase: 'L2', metric: 'current' };
  if (type === 'current_phase_c') return { phase: 'L3', metric: 'current' };
  if (type === 'current_neutral') return { phase: 'N', metric: 'current' };
  if (type === 'power_phase_a') return { phase: 'L1', metric: 'active' };
  if (type === 'power_phase_b') return { phase: 'L2', metric: 'active' };
  if (type === 'power_phase_c') return { phase: 'L3', metric: 'active' };

  const iec = (measurement.iecName ?? '').toLowerCase();
  if (iec.includes('.a.phsa')) return { phase: 'L1', metric: 'current' };
  if (iec.includes('.a.phsb')) return { phase: 'L2', metric: 'current' };
  if (iec.includes('.a.phsc')) return { phase: 'L3', metric: 'current' };
  if (iec.includes('.a.neut')) return { phase: 'N', metric: 'current' };
  if (iec.includes('.w.phsa')) return { phase: 'L1', metric: 'active' };
  if (iec.includes('.w.phsb')) return { phase: 'L2', metric: 'active' };
  if (iec.includes('.w.phsc')) return { phase: 'L3', metric: 'active' };
  if (iec.includes('.var.phsa')) return { phase: 'L1', metric: 'reactive' };
  if (iec.includes('.var.phsb')) return { phase: 'L2', metric: 'reactive' };
  if (iec.includes('.var.phsc')) return { phase: 'L3', metric: 'reactive' };
  if (iec.includes('.va.phsa')) return { phase: 'L1', metric: 'apparent' };
  if (iec.includes('.va.phsb')) return { phase: 'L2', metric: 'apparent' };
  if (iec.includes('.va.phsc')) return { phase: 'L3', metric: 'apparent' };

  return null;
};

const isTotalPowerMeasurement = (measurement: OutletMeasurement) => {
  const type = (measurement.measurementType ?? '').toLowerCase();
  if (type === 'power_total') return true;
  const iec = (measurement.iecName ?? '').toLowerCase();
  return iec.includes('totw');
};

const isTotalReactiveMeasurement = (measurement: OutletMeasurement) => {
  const type = (measurement.measurementType ?? '').toLowerCase();
  if (type === 'reactive_power_total') return true;
  const iec = (measurement.iecName ?? '').toLowerCase();
  return iec.includes('totvar');
};

const isTotalApparentMeasurement = (measurement: OutletMeasurement) => {
  const type = (measurement.measurementType ?? '').toLowerCase();
  if (type === 'apparent_power_total') return true;
  const iec = (measurement.iecName ?? '').toLowerCase();
  // Match 'totva' but exclude 'totvar' (reactive power)
  return iec.includes('totva') && !iec.includes('totvar');
};

const isAverageMeasurement = (measurement: OutletMeasurement) => {
  const iec = (measurement.iecName ?? '').toLowerCase();
  if (!iec) return true;
  return !iec.includes('maxmmxu') && !iec.includes('minmmxu');
};

const isAverageTotalMeasurement = (
  measurement: OutletMeasurement,
  predicate: (m: OutletMeasurement) => boolean
) => {
  // Check if measurement matches the predicate and is not a min/max aggregate
  return predicate(measurement) && isAverageMeasurement(measurement);
};

const getLatestMeasurement = (
  measurements: OutletMeasurement[],
  predicate: (m: OutletMeasurement) => boolean
) => {
  let latest: OutletMeasurement | null = null;
  let latestTs = 0;
  let sawValidTimestamp = false;

  for (const measurement of measurements) {
    if (!predicate(measurement)) continue;
    const ts = Date.parse(measurement.timestamp);
    if (Number.isFinite(ts)) {
      sawValidTimestamp = true;
      if (!latest || ts > latestTs) {
        latest = measurement;
        latestTs = ts;
      }
    } else if (!sawValidTimestamp && !latest) {
      // Fallback: keep first matching value if timestamps are not parseable.
      latest = measurement;
    }
  }

  return latest;
};

export const TrafostationDetails: React.FC<Props> = ({ element: initialElement }) => {
  const elements = useNetworkElementsStore(state => state.elements);
  const getElement = useNetworkElementsStore(state => state.getElement);
  const powerMeasurements = useNetworkElementsStore(state => state.powerMeasurements);
  useNetworkDataPolling(15000);

  const element = getElement(initialElement.id) as TrafoStation;
  const [lemonbeatData, setLemonbeatData] = useState<LinkedAmqpOutletsResponse | null>(null);
  const [lemonbeatError, setLemonbeatError] = useState<string | null>(null);
  const [isLemonbeatLoading, setIsLemonbeatLoading] = useState(false);

  // Subscribe to load flow updates so Lemonbeat data refreshes in sync with power flow
  const loadFlowLastUpdated = useLoadFlowStore(state => state.lastUpdated);

  const reloadLemonbeatData = useCallback(async () => {
    setIsLemonbeatLoading(true);
    setLemonbeatError(null);
    try {
      const linked = await getLinkedOutletsByTrafo(initialElement.id);
      setLemonbeatData(linked ?? null);
    } catch (err: any) {
      // ApiError has status directly, axios errors have response.status
      const status = err?.status ?? err?.response?.status;
      if (status === 404) {
        setLemonbeatData(null);
      } else {
        setLemonbeatError('Fehler beim Laden der Trafo-Messdaten.');
      }
    } finally {
      setIsLemonbeatLoading(false);
    }
  }, [initialElement.id]);

  // Initial fetch on mount
  useEffect(() => {
    reloadLemonbeatData();
  }, [reloadLemonbeatData]);

  // Re-fetch Lemonbeat data whenever power flow results update (same 10s cycle)
  const isInitialMount = useRef(true);
  useEffect(() => {
    // Skip the first render (already handled by the mount effect above)
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    if (loadFlowLastUpdated) {
      reloadLemonbeatData();
    }
  }, [loadFlowLastUpdated, reloadLemonbeatData]);

  // Find connected cables using busIndex from cable_segments (from_bus/to_bus)
  const allCables = useMemo(() =>
    elements.filter((el): el is Abgangsleitung => el.type === 'abgangsleitung'),
    [elements]
  );

  const connectedLines = useMemo(() => {
    if (!element) return [];
    return findCablesConnectedToTrafo(element, allCables);
  }, [element, allCables]);

  const trafoOutlet = useMemo(() => {
    if (!lemonbeatData?.outlets?.length) return null;
    // First try to find by trafoOutletId
    if (lemonbeatData.trafoOutletId) {
      const byId = lemonbeatData.outlets.find(outlet => outlet.id === lemonbeatData.trafoOutletId);
      if (byId) return byId;
    }
    // Second: find by deviceFamily === 'trafo' (consistent with isTrafoOutlet in LemonbeatMeasurementsSection)
    const byFamily = lemonbeatData.outlets.find(outlet =>
      (outlet.deviceFamily ?? '').toLowerCase() === 'trafo'
    );
    if (byFamily) return byFamily;
    // Fallback: find outlet without linkedCableId (that's the trafo measurement, not an Abgang)
    return lemonbeatData.outlets.find(outlet => !outlet.linkedCableId) ?? null;
  }, [lemonbeatData]);

  const trafoMeasurements = useMemo(() => {
    return trafoOutlet?.measurements ?? [];
  }, [trafoOutlet]);

  const phaseSummary = useMemo<PhaseSummary>(() => {
    const summary = createEmptyPhaseSummary();

    const latestPhaseMetrics = new Map<string, { measurement: OutletMeasurement; timestamp: number | null }>();

    for (const measurement of trafoMeasurements) {
      if (!isAverageMeasurement(measurement)) continue;
      const mapping = getPhaseMetric(measurement);
      if (!mapping) continue;

      const ts = Date.parse(measurement.timestamp);
      const key = `${mapping.phase}-${mapping.metric}`;
      const existing = latestPhaseMetrics.get(key);
      if (Number.isFinite(ts)) {
        if (!existing || (existing.timestamp !== null && ts > existing.timestamp)) {
          latestPhaseMetrics.set(key, { measurement, timestamp: ts });
        }
      } else if (!existing) {
        // Fallback to first seen value if timestamps are not parseable.
        latestPhaseMetrics.set(key, { measurement, timestamp: null });
      }
    }

    for (const [key, entry] of latestPhaseMetrics.entries()) {
      const [phase, metric] = key.split('-') as [PhaseKey, 'current' | 'active' | 'reactive' | 'apparent'];
      const value = Number(entry.measurement.value);
      if (!Number.isFinite(value)) continue;

      if (metric === 'current') {
        summary[phase].current = {
          value: convertCurrentToA(value, entry.measurement.unit),
          unit: 'A'
        };
      } else if (metric === 'active') {
        summary[phase].activePower = {
          value: convertPowerToKw(value, entry.measurement.unit),
          unit: 'kW'
        };
      } else if (metric === 'reactive') {
        summary[phase].reactivePower = {
          value: convertReactiveToKvar(value, entry.measurement.unit),
          unit: 'kvar'
        };
      } else if (metric === 'apparent') {
        summary[phase].apparentPower = {
          value: convertApparentToKva(value, entry.measurement.unit),
          unit: 'kVA'
        };
      }
    }

    return summary;
  }, [trafoMeasurements]);

  const totalActivePowerKw = useMemo(() => {
    const totalMeasurement = getLatestMeasurement(
      trafoMeasurements,
      m => isAverageTotalMeasurement(m, isTotalPowerMeasurement)
    );
    if (totalMeasurement) {
      return convertPowerToKw(Number(totalMeasurement.value), totalMeasurement.unit);
    }
    return (phaseSummary.L1.activePower?.value ?? 0)
      + (phaseSummary.L2.activePower?.value ?? 0)
      + (phaseSummary.L3.activePower?.value ?? 0);
  }, [phaseSummary, trafoMeasurements]);

  const totalReactivePowerKvar = useMemo(() => {
    const totalMeasurement = getLatestMeasurement(
      trafoMeasurements,
      m => isAverageTotalMeasurement(m, isTotalReactiveMeasurement)
    );
    if (totalMeasurement) {
      return convertReactiveToKvar(Number(totalMeasurement.value), totalMeasurement.unit);
    }
    return (phaseSummary.L1.reactivePower?.value ?? 0)
      + (phaseSummary.L2.reactivePower?.value ?? 0)
      + (phaseSummary.L3.reactivePower?.value ?? 0);
  }, [phaseSummary, trafoMeasurements]);

  const totalApparentPowerKva = useMemo(() => {
    const totalMeasurement = getLatestMeasurement(
      trafoMeasurements,
      m => isAverageTotalMeasurement(m, isTotalApparentMeasurement)
    );
    if (totalMeasurement) {
      return convertApparentToKva(Number(totalMeasurement.value), totalMeasurement.unit);
    }
    return (phaseSummary.L1.apparentPower?.value ?? 0)
      + (phaseSummary.L2.apparentPower?.value ?? 0)
      + (phaseSummary.L3.apparentPower?.value ?? 0);
  }, [phaseSummary, trafoMeasurements]);

  const isHistorical = useHistoricalPlaybackStore(state => state.mode) === 'historical';
  const effectiveTs = useHistoricalPlaybackStore(state => state.effectiveTs);
  const [historyModalOpen, setHistoryModalOpen] = useState(false);
  const [stationDiagramOpen, setStationDiagramOpen] = useState(false);

  if (!element) {
    console.error('[TrafostationDetails] Element not found in store:', initialElement.id);
    return null;
  }

  // element.capacity is delivered in VA (backend TrafoStation.Capacity);
  // Lemonbeat reports its rating in kVA. Normalize to kVA for display.
  const capacityKva = lemonbeatData?.ratingPowerKva ?? (element.capacity > 10_000 ? element.capacity / 1000 : element.capacity);

  // Backend loading: measured station loading (Lemonbeat, ≤ 30 min) first, the load flow as fallback
  const powerFlowMeasurement = powerMeasurements[element.id];
  const powerFlowLoading = powerFlowMeasurement?.loadingPercent ?? undefined;

  // The Leistungsübersicht prefers live Lemonbeat totals; without a Lemonbeat
  // device (e.g. EWH zones) — and always in historical playback, where live
  // device data would contradict the shown instant — it falls back to the
  // powerflow result held in the store.
  const usePowerflowTotals = isHistorical || trafoMeasurements.length === 0;
  const displayActivePowerKw = usePowerflowTotals
    ? (powerFlowMeasurement?.totalActivePower ?? 0)
    : totalActivePowerKw;
  const displayReactivePowerKvar = usePowerflowTotals
    ? (powerFlowMeasurement?.totalReactivePowerKvar ?? 0)
    : totalReactivePowerKvar;
  const displayApparentPowerKva = usePowerflowTotals
    ? (powerFlowMeasurement?.totalApparentPowerKva ?? 0)
    : totalApparentPowerKva;

  return (
    <div className={`flex flex-col ${detailStyles.layout.section}`}>
      {/* Header Section */}
      <div className={`${detailStyles.header.wrapper} ${headerColors.trafostation.bg}`}>
        <div className={detailStyles.header.content}>
          <div className="flex items-center space-x-3">
            <div className={detailStyles.header.iconBox}>
              <svg className={`${detailStyles.header.icon} ${headerColors.trafostation.icon}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
            </div>
            <div>
              <h2 className={detailStyles.header.title}>Trafostation</h2>
              <p className={detailStyles.header.subtitle}>{element.name}</p>
            </div>
          </div>
        </div>
        <div className={`${detailStyles.header.id} ${headerColors.trafostation.id}`}>ID: {element.id}</div>
      </div>

      {/* Power Flow Metrics - Lemonbeat totals when available, powerflow result otherwise / in playback */}
      <PowerFlowMetrics
        activePowerKw={displayActivePowerKw}
        reactivePowerKvar={displayReactivePowerKvar}
        apparentPowerKva={displayApparentPowerKva}
        capacityKva={capacityKva}
        loadingPercent={powerFlowLoading}
      />

      {/* Per-phase U/I/P, cos φ, f and the feeders — measurement first, calculation next to it */}
      <TrafoElectricalSection trafo={element} isHistorical={isHistorical} effectiveTs={effectiveTs} />

      {/* Trennstellen whose fuse sits in this station (coupling cables of neighbour zones) */}
      <CouplingsSection elementId={element.id} />

      {/* Betriebsfaktor for the §14a trigger (control module only) */}
      <OperationalRatingFactorField elementId={element.id} factor={element.operationalRatingFactor} ratingOf="der Nennleistung" />

      <div className="grid grid-cols-2 gap-2">
        <button
          onClick={() => setStationDiagramOpen(true)}
          className="px-3 py-2 text-sm rounded-md border border-gray-200 dark:border-primary-600 bg-white dark:bg-primary-800 text-gray-700 dark:text-primary-200 hover:bg-gray-50 dark:hover:bg-primary-700 transition-colors flex items-center justify-center gap-2 cursor-pointer"
          title="Übersichtsschaltplan der Station: Trafo, Sammelschiene, Abgänge mit Sicherung, Kabel und Ziel"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 5h16M12 5v6m-6 0h12M6 11v8m6-8v8m6-8v8" />
          </svg>
          Stationsansicht
        </button>
        <button
          onClick={() => setHistoryModalOpen(true)}
          className="px-3 py-2 text-sm rounded-md border border-gray-200 dark:border-primary-600 bg-white dark:bg-primary-800 text-gray-700 dark:text-primary-200 hover:bg-gray-50 dark:hover:bg-primary-700 transition-colors flex items-center justify-center gap-2 cursor-pointer"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
          </svg>
          Lastfluss-Historie
        </button>
      </div>

      <StationDiagramModal
        isOpen={stationDiagramOpen}
        element={element}
        lemonbeatData={lemonbeatData}
        onClose={() => setStationDiagramOpen(false)}
      />

      <ElementHistoryModal
        isOpen={historyModalOpen}
        elementId={element.id}
        elementName={shortElementName(element) ?? element.name}
        showLoading
        showVoltage
        showMeasuredTrafo
        onClose={() => setHistoryModalOpen(false)}
      />

      {isHistorical ? (
        <div className="bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-md p-3 text-xs text-orange-800 dark:text-orange-200">
          Historische Ansicht: Leistungsübersicht und Elektrische Größen zeigen
          den gespeicherten Stand des gewählten Zeitpunkts (Trafo-Messung, soweit
          vorhanden, und Lastfluss). Live-Messdaten sind ausgeblendet.
        </div>
      ) : (
        <LemonbeatMeasurementsSection
          linkedData={lemonbeatData}
          isLoading={isLemonbeatLoading}
          error={lemonbeatError}
          phaseSummary={phaseSummary}
          trafoId={element.id}
          connectedLines={connectedLines}
          onReload={reloadLemonbeatData}
        />
      )}

    </div>
  );
};


